import {defineAsyncComponent, type Component} from 'vue';
import * as Sentry from '@sentry/vue';
import ActionComponent = App.DTOs.Menus.ActionComponent;

/**
 * Components an `ActionComponent` menu action can name, keyed by its `name`.
 *
 * A registered component receives the action's `props` plus the header's
 * `size`, and nothing else, so it has to be self-contained (own modal, own
 * request, own translations) and declare a `size` prop so it matches the
 * surrounding actions.
 *
 * Entries are registered lazily: the header is part of the app layout, so an
 * eager import would pull every registered component — and its dependencies —
 * into the entry chunk downloaded on every authenticated page.
 */
const actionComponents: Record<string, Component> = {
  'copy-task': defineAsyncComponent(() => import('../buttons/CopyTaskButton.vue')),
};

export const isActionComponent = (action: object): action is ActionComponent =>
  'type' in action && action.type === 'button';

// Names we've already reported, so a re-render doesn't re-send the same error.
const reportedNames = new Set<string>();

/**
 * Resolves the component an action names, or `null` when the backend names one
 * that isn't registered — the action is then skipped rather than rendered as an
 * empty button.
 */
export const resolveActionComponent = (name: string): Component | null => {
  const component = actionComponents[name];

  if (!component) {
    if (!reportedNames.has(name)) {
      reportedNames.add(name);
      Sentry.captureException(new Error(`Unregistered menu action component "${name}"`));
    }

    return null;
  }

  return component;
};
